import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Delete, Edit, Refresh } from '@mui/icons-material';
import {
  fetchBanners,
  createBanner,
  updateBanner,
  deleteBanner,
  toggleBanner,
} from '../../services/banner';

const defaultFormState = {
  title: '',
  description: '',
  cta_url: '',
  sort_order: 0,
  is_active: true,
  starts_at: '',
  ends_at: '',
};

const BannerManagement = () => {
  const { t } = useTranslation(['banners', 'common']);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [editingBanner, setEditingBanner] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total_pages: 1,
    count: 0,
    has_next: false,
    has_previous: false,
  });

  const pageSize = 10;

  const loadBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchBanners({ page, page_size: pageSize });
      setBanners(response.results || []);
      setPagination({
        total_pages: response.total_pages || 1,
        count: response.count || 0,
        has_next: response.has_next,
        has_previous: response.has_previous,
      });
    } catch (error) {
      console.error('Failed to fetch banners:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('banners:messages.failedToFetch'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const resetForm = useCallback(() => {
    setFormState(defaultFormState);
    setSelectedImage(null);
    setPreviewUrl('');
    setEditingBanner(null);
  }, []);

  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append('title', formState.title || '');
    formData.append('description', formState.description || '');
    formData.append('cta_url', formState.cta_url || '');
    formData.append('sort_order', formState.sort_order || 0);
    formData.append('is_active', formState.is_active ? 'true' : 'false');
    if (formState.starts_at) {
      formData.append('starts_at', formState.starts_at);
    } else {
      formData.append('starts_at', '');
    }
    if (formState.ends_at) {
      formData.append('ends_at', formState.ends_at);
    } else {
      formData.append('ends_at', '');
    }
    if (selectedImage) {
      formData.append('image', selectedImage);
    }
    return formData;
  };

  const handleSubmit = async (fromModal = false) => {
    // If editing, only allow submit from modal
    if (editingBanner && !fromModal) {
      return;
    }

    if (!editingBanner && !selectedImage) {
      setSnackbar({ open: true, message: t('banners:messages.pleaseSelectImage'), severity: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const formData = buildFormData();
      if (editingBanner) {
        await updateBanner(editingBanner.id, formData, true);
        setSnackbar({ open: true, message: t('banners:messages.bannerUpdatedSuccess'), severity: 'success' });
      } else {
        await createBanner(formData);
        setSnackbar({ open: true, message: t('banners:messages.bannerCreatedSuccess'), severity: 'success' });
      }
      resetForm();
      if (editingBanner) {
        setEditModalOpen(false);
      }
      loadBanners();
    } catch (error) {
      console.error('Banner save error:', error);
      const message =
        error.response?.data?.detail ||
        error.response?.data?.image?.[0] ||
        t('banners:messages.failedToSave');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormState({
      title: banner.title || '',
      description: banner.description || '',
      cta_url: banner.cta_url || '',
      sort_order: banner.sort_order ?? 0,
      is_active: banner.is_active,
      starts_at: banner.starts_at ? banner.starts_at.slice(0, 16) : '',
      ends_at: banner.ends_at ? banner.ends_at.slice(0, 16) : '',
    });
    setPreviewUrl(banner.image_url || '');
    setSelectedImage(null);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    resetForm();
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm(t('banners:messages.deleteConfirm'))) {
      return;
    }
    try {
      await deleteBanner(bannerId);
      setSnackbar({ open: true, message: t('banners:messages.bannerDeletedSuccess'), severity: 'info' });
      if (editingBanner?.id === bannerId) {
        resetForm();
      }
      loadBanners();
    } catch (error) {
      console.error('Delete banner error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('banners:messages.failedToDelete'),
        severity: 'error',
      });
    }
  };

  const handleToggle = async (banner) => {
    try {
      await toggleBanner(banner.id, { is_active: !banner.is_active });
      setSnackbar({
        open: true,
        message: !banner.is_active ? t('banners:messages.bannerActivatedSuccess') : t('banners:messages.bannerDeactivatedSuccess'),
        severity: 'success',
      });
      loadBanners();
    } catch (error) {
      console.error('Toggle banner error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('banners:messages.failedToToggle'),
        severity: 'error',
      });
    }
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const activeBannerCount = useMemo(
    () => banners.filter((banner) => banner.is_active).length,
    [banners]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('banners:title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('banners:description')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadBanners}
          disabled={loading}
        >
          {t('banners:refresh')}
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('banners:createNewBanner')}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label={t('banners:form.title')}
                  value={formState.title}
                  onChange={handleFormChange('title')}
                  fullWidth
                />
                <TextField
                  label={t('banners:form.description')}
                  value={formState.description}
                  onChange={handleFormChange('description')}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <TextField
                  label={t('banners:form.ctaUrl')}
                  value={formState.cta_url}
                  onChange={handleFormChange('cta_url')}
                  placeholder={t('banners:form.ctaUrlPlaceholder')}
                  fullWidth
                />
                <TextField
                  label={t('banners:form.sortOrder')}
                  type="number"
                  value={formState.sort_order}
                  onChange={handleFormChange('sort_order')}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label={t('banners:form.startTime')}
                  type="datetime-local"
                  value={formState.starts_at}
                  onChange={handleFormChange('starts_at')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label={t('banners:form.endTime')}
                  type="datetime-local"
                  value={formState.ends_at}
                  onChange={handleFormChange('ends_at')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.is_active}
                      onChange={handleFormChange('is_active')}
                      color="primary"
                    />
                  }
                  label={t('banners:form.active')}
                />

                <Button variant="outlined" component="label">
                  {selectedImage ? t('banners:form.changeImage') : t('banners:form.selectImage')}
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
                {previewUrl && (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={t('banners:form.bannerPreview')}
                    sx={{ width: '100%', borderRadius: 1 }}
                  />
                )}

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving}
                  fullWidth
                >
                  {saving ? t('banners:form.saving') : t('banners:form.createBanner')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={2}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('banners:existingBanners')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pagination.count} {t('banners:total')} • {activeBannerCount} {t('banners:active')}
                  </Typography>
                </Box>
              </Stack>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('banners:table.preview')}</TableCell>
                      <TableCell>{t('banners:table.title')}</TableCell>
                      <TableCell>{t('banners:table.status')}</TableCell>
                      <TableCell>{t('banners:table.schedule')}</TableCell>
                      <TableCell>{t('banners:table.sort')}</TableCell>
                      <TableCell align="right">{t('banners:table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t('banners:table.loading')}
                        </TableCell>
                      </TableRow>
                    ) : banners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t('banners:table.noBannersFound')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      banners.map((banner) => (
                        <TableRow key={banner.id}>
                          <TableCell width={120}>
                            {banner.image_url ? (
                              <Box
                                component="img"
                                src={banner.image_url}
                                alt={banner.title}
                                sx={{
                                  width: 100,
                                  height: 56,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('banners:table.noImage')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">{banner.title}</Typography>
                            {banner.description && (
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {banner.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={banner.is_active ? t('banners:status.active') : t('banners:status.inactive')}
                              color={banner.is_active ? 'success' : 'default'}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <Switch
                              size="small"
                              checked={banner.is_active}
                              onChange={() => handleToggle(banner)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {banner.starts_at
                                ? new Date(banner.starts_at).toLocaleString()
                                : t('banners:table.immediate')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {banner.ends_at
                                ? new Date(banner.ends_at).toLocaleString()
                                : t('banners:table.noExpiry')}
                            </Typography>
                          </TableCell>
                          <TableCell>{banner.sort_order}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('banners:tooltips.editBanner')}>
                              <IconButton size="small" onClick={() => handleEdit(banner)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('banners:tooltips.deleteBanner')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(banner.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination.total_pages > 1 && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Pagination
                    count={pagination.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Banner Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingBanner ? t('banners:dialogs.edit.editTitleWithName', { title: editingBanner.title || 'Untitled' }) : t('banners:dialogs.edit.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('banners:form.title')}
              value={formState.title}
              onChange={handleFormChange('title')}
              fullWidth
            />
            <TextField
              label={t('banners:form.description')}
              value={formState.description}
              onChange={handleFormChange('description')}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label={t('banners:form.ctaUrl')}
              value={formState.cta_url}
              onChange={handleFormChange('cta_url')}
              placeholder={t('banners:form.ctaUrlPlaceholder')}
              fullWidth
            />
            <TextField
              label={t('banners:form.sortOrder')}
              type="number"
              value={formState.sort_order}
              onChange={handleFormChange('sort_order')}
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label={t('banners:form.startTime')}
              type="datetime-local"
              value={formState.starts_at}
              onChange={handleFormChange('starts_at')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('banners:form.endTime')}
              type="datetime-local"
              value={formState.ends_at}
              onChange={handleFormChange('ends_at')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formState.is_active}
                  onChange={handleFormChange('is_active')}
                  color="primary"
                />
              }
              label={t('banners:form.active')}
            />
            <Button variant="outlined" component="label">
              {selectedImage ? t('banners:form.changeImage') : t('banners:form.selectNewImage')}
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
            {(previewUrl || editingBanner?.image_url) && (
              <Box
                component="img"
                src={previewUrl || editingBanner?.image_url}
                alt={t('banners:form.bannerPreview')}
                sx={{ width: '100%', borderRadius: 1, maxHeight: 300, objectFit: 'contain' }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary">
            {t('banners:dialogs.edit.cancel')}
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            variant="contained"
            disabled={saving}
          >
            {saving ? t('banners:dialogs.edit.saving') : t('banners:dialogs.edit.updateBanner')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannerManagement;

